import { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { submitQuery } from '../api';
import { waLink, fillTemplate } from './WhatsAppFloat';
import { useSettings } from '../context/SettingsContext';

/**
 * Renders the admin-configurable enquiry form.
 * `fields` come from /api/form-fields. On submit it posts to /api/queries,
 * and also offers a "Send on WhatsApp" shortcut using the admin number.
 */
export default function DynamicForm({ fields = [], source = 'contact', refId, refName, onDone, single = false }) {
  const { settings } = useSettings();
  const [values, setValues] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const setVal = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const toggleCheck = (key, opt) => {
    setValues((v) => {
      const arr = Array.isArray(v[key]) ? v[key] : [];
      return { ...v, [key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
    });
  };

  const validate = () => {
    for (const f of fields) {
      if (f.required) {
        const val = values[f.key];
        const empty = val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
        if (empty) return `${f.label} is required`;
      }
    }
    return null;
  };

  const buildMessage = () => {
    const lines = fields
      .map((f) => {
        const val = values[f.key];
        if (val === undefined || val === '' || (Array.isArray(val) && !val.length)) return null;
        return `${f.label}: ${Array.isArray(val) ? val.join(', ') : val}`;
      })
      .filter(Boolean);
    // Heading template is admin-authored (Settings → WhatsApp).
    const tpl = settings?.whatsappEnquiryHeading || 'Enquiry about {item}';
    const head = refName ? fillTemplate(tpl, { item: refName }) : 'New enquiry';
    return `${head}\n` + lines.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    setStatus('sending');
    try {
      await submitQuery({
        data: values,
        name: values.name,
        email: values.email,
        phone: values.phone,
        message: values.message,
        source,
        refId,
        refName,
      });
      setStatus('done');
      onDone && onDone();
    } catch (e2) {
      setStatus('error');
      setError('Something went wrong. Please try again.');
    }
  };

  const sendWhatsApp = () => {
    const link = waLink(settings?.whatsappNumber, buildMessage());
    if (link) window.open(link, '_blank');
    // also record the enquiry
    submitQuery({ data: values, source, refId, refName, message: buildMessage() }).catch(() => {});
  };

  if (status === 'done') {
    return (
      <div className="empty">
        <div className="empty__icon">✅</div>
        <h3>Thank you!</h3>
        <p>Your enquiry has been received. We'll get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form className={`dform ${single ? 'dform--single' : ''}`} onSubmit={handleSubmit}>
      {fields.map((f) => {
        const cls = single || f.fullWidth || f.type === 'textarea' ? 'field full' : 'field';
        return (
          <div className={cls} key={f._id || f.key}>
            <label>
              {f.label} {f.required && <span className="req">*</span>}
            </label>

            {['text', 'email', 'tel', 'number'].includes(f.type) && (
              <input
                className="input"
                type={f.type}
                placeholder={f.placeholder}
                value={values[f.key] || ''}
                onChange={(e) => setVal(f.key, e.target.value)}
              />
            )}

            {f.type === 'textarea' && (
              <textarea
                className="textarea"
                placeholder={f.placeholder}
                value={values[f.key] || ''}
                onChange={(e) => setVal(f.key, e.target.value)}
              />
            )}

            {f.type === 'select' && (
              <select className="select" value={values[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)}>
                <option value="">{f.placeholder || 'Select…'}</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}

            {f.type === 'radio' && (
              <div className="radio-row">
                {f.options.map((o) => (
                  <label key={o} className="opt-pill">
                    <input type="radio" name={f.key} checked={values[f.key] === o} onChange={() => setVal(f.key, o)} />
                    {o}
                  </label>
                ))}
              </div>
            )}

            {f.type === 'checkbox' && (
              <div className="check-row">
                {f.options.map((o) => (
                  <label key={o} className="opt-pill">
                    <input
                      type="checkbox"
                      checked={Array.isArray(values[f.key]) && values[f.key].includes(o)}
                      onChange={() => toggleCheck(f.key, o)}
                    />
                    {o}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {error && <p className="full" style={{ color: 'var(--danger)' }}>{error}</p>}

      <div className="full flex gap wrap">
        <button type="submit" className="btn btn-primary" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Submit Enquiry'}
        </button>
        {settings?.whatsappNumber && (
          <button type="button" className="btn" style={{ background: 'var(--whatsapp)', color: '#fff' }} onClick={sendWhatsApp}>
            <FaWhatsapp /> Send on WhatsApp
          </button>
        )}
      </div>
    </form>
  );
}
